import React, { useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider } from 'react-apollo';
import Slider from 'react-slick';
import { hot } from 'react-hot-loader/root';
import ReactSVG from 'react-svg';
import chevronSmall from 'chevron-small.svg';

import calendar from 'calendar.svg';
import building from 'building.svg';
import golf from 'running.svg';
import shopping_bag from 'shopping_bag.svg';
import silverware from 'silverware.svg';
import food_platter from 'food_platter.svg';
import useWindowWidth from '../hooks/useWindowWidth';
import useSlides from '../hooks/useSlides';
import EventCard from './EventCard';
import ListingCard from './ListingCard';
import SliderArrow from './SliderArrow';
import RentalCard from './RentalCard';
import GuideCard from './GuideCard';
import LoadingCard from './Loading/LoadingCard';
import { client } from './client';

const icons = {
  calendar,
  building,
  golf,
  shopping_bag,
  silverware,
  food_platter,
};

// Used to calculate number of cards inside the slider (include 10px padding)
const cardWidths = {
  events: 220, // width 200px + (padding 10px * 2) = 220px total
  loading: 220, // width 200px + (padding 10px * 2) = 220px total
  rentals: 293,
  guides: 348,
  rentals_small: 224,
  listings: 293,
  listings_small: 224,
};

function ListingTypeSlider({
  listings,
  title,
  icon,
  listing_type,
  display,
  link,
}) {
  const sliderRef = useRef(null);
  const windowWidth = useWindowWidth();

  if (!cardWidths[listing_type]) {
    console.error(`Card width not set for ${listing_type}`);
  }

  if (icon && !icons[icon]) {
    console.error(`Icon \`${icon}\` was not imported or declared in icons`);
  }

  const slideCount = useSlides({
    sliderRef,
    cardWidth: cardWidths[listing_type],
  });

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: slideCount,
    slidesToScroll: slideCount,
    arrows: true,
    prevArrow: (
      <SliderArrow direction="prev" sliderRef={sliderRef} type={listing_type} />
    ),
    nextArrow: (
      <SliderArrow direction="next" sliderRef={sliderRef} type={listing_type} />
    ),
  };

  const cards = [];
  const sliderBreakpoint = 768;

  const sortPremier = (a, b) => {
    if (!a.is_premier && b.is_premier) return 1;
    if (a.is_premier && !b.is_premier) return -1;
    return 0;
  };

  switch (listing_type) {
    case 'events':
      listings.forEach(event => {
        cards.push(<EventCard key={event.id} event={event} />);
      });
      break;

    case `rentals`:
      listings.forEach(property => {
        cards.push(<RentalCard key={property.id} property={property} />);
      });
      break;

    case `rentals_small`:
      listings.forEach(property => {
        cards.push(
          <RentalCard key={property.id} property={property} is_small />
        );
      });
      break;

    case `listings`:
      listings.sort(sortPremier).forEach(listing => {
        cards.push(<ListingCard key={listing.id} listing={listing} />);
      });
      break;

    case `listings_small`:
      listings.sort(sortPremier).forEach(listing => {
        cards.push(<ListingCard key={listing.id} listing={listing} is_small />);
      });
      break;

    case `guides`:
      listings.forEach(listing => {
        cards.push(<GuideCard key={listing.id} guide={listing} />);
      });
      break;

    case `loading`:
      [...Array(5)].forEach((_, ind) => {
        cards.push(<LoadingCard key={ind} card_type="events" />);
      });
      break;

    default:
      console.error(`Case for listing type \`${listing_type}\` is not handled`);
      break;
  }

  const roundDownToNearestTenth = Math.floor(cards.length / 10) * 10;

  // Adds extra padding to end of slider on mobile screens
  if (
    cards[cards.length - 1] &&
    cards[cards.length - 1].type !== 'span' &&
    windowWidth <= 768
  ) {
    cards.push(<span key="9999" className="end-slider-padding" />);
  }

  return (
    <ApolloProvider client={client}>
      <section className="slider-container" ref={sliderRef}>
        <header className="slider-header">
          <div className="title">
            {icon && <ReactSVG src={icons[icon]} />}
            {title && (
              <h2>
                <a
                  href={link}
                  title={`View All ${listing_type}`}
                  aria-label={`View All ${listing_type}`}
                >
                  {title.split('-').join(' ')}
                </a>
              </h2>
            )}
            {listing_type === 'loading' && (
              <div className="loading-text title" />
            )}
          </div>
          {listing_type !== '' && (
            <div className="view-all" style={{ display }}>
              <a
                className="secondary-text"
                href={link}
                aria-label={`View All ${listing_type}`}
              >
                View All (
                {cards.length <= 10
                  ? cards.length
                  : `${roundDownToNearestTenth}+`}
                )
              </a>
              <ReactSVG src={chevronSmall} />
            </div>
          )}
        </header>
        {cards.length > 0 ? (
          <Fragment>
            {windowWidth > sliderBreakpoint ? (
              <Slider {...settings}>{cards}</Slider>
            ) : (
              <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  {cards}
                </div>
              </div>
            )}
          </Fragment>
        ) : (
          <div className="slider-fallback">
            <p>No {title} Available</p>
          </div>
        )}
      </section>
    </ApolloProvider>
  );
}

export default hot(ListingTypeSlider);

ListingTypeSlider.propTypes = {
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  listings: PropTypes.array,
  title: PropTypes.string,
  icon: PropTypes.string,
  listing_type: PropTypes.string.isRequired,
  display: PropTypes.string,
  link: PropTypes.string,
};
